import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { RisksColumns } from "@/modules/agent/components/TPRADashboard/constants/risksColumns";
import { useRisksServerSideState } from "@/modules/agent/components/TPRADashboard/hooks/useServerSideState";
import { useGetRisks } from "@/modules/agent/components/TPRADashboard/requests/getRisks";
import type { RiskItem } from "@/modules/agent/components/TPRADashboard/types/risks";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { cn, Select, SelectItem, type SortDescriptor } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export default function TPRAIssuesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Server-side state management
  const { queryParams, pagination, search, sort, severity } = useRisksServerSideState({
    page: 1,
    page_size: 25,
    sort_by: "reporting_date",
    sort_order: "desc",
  });

  // Apply URL params on mount (from Vendors page navigation)
  useEffect(() => {
    const vendorParam = searchParams.get("vendor");
    const severityParam = searchParams.get("severity");

    if (vendorParam) {
      search.setSearchTerm(vendorParam);
    }

    if (severityParam) {
      const severityValue = severityParam as "Critical" | "High" | "Medium" | "Low";
      severity.setValue(severityValue);
    }

    // Clear URL params after applying (optional - keeps URL clean)
    if (vendorParam || severityParam) {
      setSearchParams({}, { replace: true });
    }
  }, []); // Run only on mount

  // Fetch risks from API
  const { data, isLoading, isFetching, isError, error } = useGetRisks(queryParams);

  // Extract data with defaults
  const risks = data?.risks ?? [];
  const totalIssues = data?.total_issues ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const currentPage = data?.page ?? 1;

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  // Handle sort changes from table
  const handleSortModelChange = useCallback(
    (sortDescriptor: SortDescriptor) => {
      const column = String(sortDescriptor.column);

      if (!column) {
        sort.resetSort();
        return;
      }

      // Map column names to API sort fields
      const columnToFieldMap: Record<string, string> = {
        third_party_product: "vendor_name",
        reporting_date: "reporting_date",
        severity: "severity",
      };

      const sortBy = columnToFieldMap[column];

      if (sortBy) {
        sort.setSort(sortBy as any);
      }
    },
    [sort]
  );

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
                { title: "Issues", href: `/${ROUTES.THIRD_PARTY_ISSUES}` },
              ]}
            />
          </div>
          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleNewAssessment}
          >
            New Assessment
          </ZButton>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          {/* Header: Title + Count | Severity Filter + Search */}
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
              {/* Left: Title + Count */}
              <div className="flex flex-row items-center gap-2 flex-grow">
                <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
                  Issues
                </h2>
                {!isLoading && !isFetching && (
                  <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
                    <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                      {totalIssues}
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Severity Filter + Search */}
              <div className="flex flex-row items-center gap-2">
                <Select
                  placeholder="All Severities"
                  selectedKeys={[severity.value ?? "all"]}
                  onChange={(e) => {
                    const value = e.target.value;
                    severity.setValue(
                      value === "all"
                        ? undefined
                        : (value as "Critical" | "High" | "Medium" | "Low")
                    );
                  }}
                  className="w-[160px]"
                  classNames={{
                    trigger: "h-[36px] bg-white border-1 border-[#E4E4E7] rounded-[6px]",
                    value: "font-['Inter'] font-normal text-[14px] leading-[20px] text-[#3F3F46]",
                  }}
                >
                  <SelectItem key="all" value="all">
                    All Severities
                  </SelectItem>
                  <SelectItem key="Critical" value="Critical">
                    Critical
                  </SelectItem>
                  <SelectItem key="High" value="High">
                    High
                  </SelectItem>
                  <SelectItem key="Medium" value="Medium">
                    Medium
                  </SelectItem>
                  <SelectItem key="Low" value="Low">
                    Low
                  </SelectItem>
                </Select>

                <SearchField
                  value={search.searchTerm}
                  onChange={search.setSearchTerm}
                  placeholder="Search by name"
                  className="w-[420px]"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="relative flex-1 min-h-0">
              {/* Loading overlay - only during refetch, not initial load */}
              {!isLoading && isFetching && (
                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                  <LoadingSpinner />
                </div>
              )}
              <ZTableDeprecrated<RiskItem>
                className={cn(
                  "border-1 border-[#E4E4E7] overflow-auto scroll-auto h-full"
                )}
                layout="auto"
                loading={isLoading || isFetching}
                rows={risks}
                columns={RisksColumns}
                isHeaderSticky
                isCompact
                removeWrapper
                getRowId={(row) => row.id}
                tableHeaderProps={{ className: "shadow-none rounded-none" }}
                tableRowProps={(row, rowIndex) => ({
                  className: cn(rowIndex === 0 ? "border-none" : "border-y", "group"),
                })}
                classNames={{
                  wrapper: "!shadow-none !rounded-none",
                  base: "!shadow-none !rounded-none",
                  th: "!shadow-none !rounded-none !bg-gradient-to-b !from-[#f5f2f4] !to-[rgb(245,242,244)]",
                }}
                aria-label="Issues List"
                onSortModelChange={handleSortModelChange}
                tableBodyProps={{
                  emptyContent: isError ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-2">
                      <p className="text-sm text-gray-500">Failed to load issues</p>
                      {error && <p className="text-xs text-red-500">{error.message}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      {isLoading || isFetching ? (
                        <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
                      ) : (
                        <p className="text-sm text-gray-500">No issues found</p>
                      )}
                    </div>
                  ),
                }}
              />
            </div>
            <ZTablePagination
              totalPages={totalPages}
              paginationState={{
                currentPage: currentPage,
                rowsPerPage: pagination.pageSize,
              }}
              setPagination={{
                handlePageChange: (page) => pagination.setPage(page),
                handleRowsPerPageChange: (rows) => pagination.setPageSize(rows),
              }}
              className="px-5 !py-3 border-t border-[#E4E4E7]"
            />
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
